import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Flex,
  Form,
  FormButtons,
  FormHeader,
  Input,
  NormalizedException,
} from "@songtradr-sso/shared/frontend";
import {
  Alert,
  Body,
  Button,
  ButtonLoadingIcon,
  Checkbox,
  Heading,
} from "@songtradr/design-language";
import { PageContainer } from "../../../components/page-container/PageContainer";
import { forwardAuth } from "../../../shared/utils/api.utils";
import { FormFields, exchangeTempTokenPostMfa, schema } from "../Mfa.Parts";
import { text, verifySmsCode } from "./MfaSmsVerify.Parts";

export const MfaSmsVerify = () => {
  const formMethods = useForm<FormFields>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
  });

  const {
    setError,
    formState: { errors, isSubmitting },
  } = formMethods;

  const [rememberMe, setRememberMe] = useState(false);

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const tempToken = sessionStorage.getItem("auth_tmp") ?? "";
      const response = await verifySmsCode({
        rememberMe,
        verificationCode: data.code,
        tempToken,
      });
      await exchangeTempTokenPostMfa(tempToken, response.data.mfa_confirm_code);
      await forwardAuth();
    } catch (err) {
      setError("root", { message: new NormalizedException(err).message });
    }
  };

  return (
    <PageContainer floatTop backButton>
      <FormHeader>
        <Heading variant="h3-strong">{text.title}</Heading>
        <Body>
          {text.description.replace(
            "{finalDigits}",
            sessionStorage.getItem("phone_digits") ??
              text.finalDigitsPlaceholder,
          )}
        </Body>
      </FormHeader>
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <Heading variant="section">{text.inputLabel}</Heading>
        <Input
          id={text.shared.inputs.verificationCode.id}
          label={text.shared.inputs.verificationCode.label}
        />
        <Alert message={errors.root?.message} dismissible />
        <FormButtons>
          <Flex $gap="1rem">
            <Checkbox
              type="secondary"
              variant={rememberMe ? "checked" : "unchecked"}
              onClick={() => setRememberMe(!rememberMe)}
              disabled={isSubmitting}
            />
            <Body variant="small">{text.rememberMe}</Body>
          </Flex>
          <Button $variant="important" type="submit" disabled={isSubmitting}>
            {isSubmitting ? <ButtonLoadingIcon /> : text.button}
          </Button>
        </FormButtons>
      </Form>
    </PageContainer>
  );
};
